import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, Link, useParams } from 'react-router-dom';
import { getDigitalCards, createDigitalCard, updateDigitalCard, deleteDigitalCard, publishDigitalCard, unpublishDigitalCard, generateShareUrl } from '../utils/dataManager';
import { getCurrentUser } from '../utils/auth';
import DigitalIDForm from '../components/digitalid/DigitalIDForm';
import DigitalIDCard from '../components/digitalid/DigitalIDCard';
import { FiEdit, FiTrash2, FiEyeOff, FiEye } from 'react-icons/fi';
import { BiCopy } from 'react-icons/bi';
import './DigitalCardManagement.css';

const DigitalCardList = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadCards();
  }, []);

  const loadCards = async () => {
    setLoading(true);
    try {
      const allCards = await getDigitalCards();
      setCards(allCards || []);
    } catch (error) {
      console.error('Error loading cards:', error);
      setCards([]); 
    }
    setLoading(false);
  };

  const handleEdit = (cardId) => {
    navigate(`/digital-cards/edit/${cardId}`);
  };

  const handleDelete = async (cardId) => {
    if (window.confirm('Are you sure you want to delete this digital card?')) {
      try {
        await deleteDigitalCard(cardId);
        loadCards();
      } catch (error) {
        console.error('Error deleting card:', error);
        alert('Failed to delete card');
      }
    }
  };

  const handlePublish = async (cardId) => {
    try {
      await publishDigitalCard(cardId);
      loadCards();
    } catch (error) {
      console.error('Error publishing card:', error);
      alert('Failed to publish card');
    }
  };

  const handleUnpublish = async (cardId) => {
    try {
      await unpublishDigitalCard(cardId);
      loadCards();
    } catch (error) {
      console.error('Error unpublishing card:', error);
      alert('Failed to unpublish card');
    }
  };

  const handleShare = async (cardId) => {
    try {
      const shareUrl = await generateShareUrl(cardId);
      if (shareUrl) {
        navigator.clipboard.writeText(shareUrl).then(() => {
          alert('✅ Share link copied to clipboard!\n\nYou can now paste this link anywhere to share the digital card.');
        }).catch(() => {
          prompt('Copy this link to share:', shareUrl);
        });
      } else {
        alert('❌ Error generating share link. Please make sure the card is published.');
      }
    } catch (error) {
      console.error('Error sharing card:', error);
      alert('Failed to generate share link');
    }
  };

  const filteredCards = (cards || []).filter(card => {
    const matchesSearch = card.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         card.position?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || card.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <div className="loading">Loading digital cards...</div>;
  }

  return (
    <div className="digital-card-list">
      <div className="list-header">
        <h1>Digital ID Cards</h1>
        <Link to="/digital-cards/create" className="btn btn-primary">
          Create New Card
        </Link>
      </div>

      <div className="list-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name or position..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="status-filter">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {filteredCards.length === 0 ? (
        <div className="empty-state">
          <p>No digital cards found.</p>
          <Link to="/digital-cards/create" className="btn btn-primary">
            Create Your First Card
          </Link>
        </div>
      ) : (
        <div className="cards-grid">
          {filteredCards.map(card => (
            <div key={card.id} className="card-item">
              <div className="card-info">
                <h3>{card.name}</h3>
                <p className="position">{card.position}</p>
                <p className="company">{card.company_name || 'Jethero HQ'}</p>
                
                <div className="card-meta">
                  <span className={`status-badge ${card.status}`}>
                    {card.status === 'published' ? 'Published' : 'Draft'}
                  </span>
                  <span className="created-date">
                    {new Date(card.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              
              <div className="card-actions">
                <button onClick={() => handleEdit(card.id)} className="btn btn-primary btn-sm" title="Edit">
                  <FiEdit />
                </button>
                
                {card.status === 'published' ? (
                  <>
                    <button onClick={() => handleUnpublish(card.id)} className="btn btn-secondary btn-sm" title="Unpublish">
                      <FiEyeOff />
                    </button>
                    <button 
                      onClick={() => handleShare(card.id)} 
                      className="btn btn-success btn-sm"
                      title="Copy share link to clipboard"
                    >
                      <BiCopy />
                    </button>
                  </>
                ) : (
                  <button onClick={() => handlePublish(card.id)} className="btn btn-success btn-sm" title="Publish">
                    <FiEye />
                  </button>
                )}
                
                <button onClick={() => handleDelete(card.id)} className="btn btn-danger btn-sm" title="Delete">
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const DigitalCardCreate = () => {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  const handleSave = async (cardData) => {
    try {
      if (!currentUser || !currentUser.id) {
        alert('User not logged in. Please login first.');
        navigate('/login');
        return;
      }
      
      const newCard = await createDigitalCard(cardData, currentUser.id);
      
      alert('Digital ID Card created successfully!');
      navigate('/digital-cards');
    } catch (error) {
      console.error('Error creating card:', error);
      alert('Failed to create Digital ID Card. Please try again.');
    }
  };

  const handleCancel = () => {
    navigate('/digital-cards');
  };

  return (
    <DigitalIDForm
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );
};

const DigitalCardEdit = ({ cardId }) => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadCard = async () => {
      try {
        const cards = await getDigitalCards();
        
        const cardData = (cards || []).find(c => c.id == cardId);  
        if (!cardData) {
          console.error('❌ Card not found for ID:', cardId);
        }
        
        setCard(cardData);
      } catch (error) {
        console.error('❌ Error loading card:', error);
        setCard(null);
      }
      setLoading(false);
    };
    
    loadCard();
  }, [cardId]);

  const handleSave = async (cardData) => {
    try {
      const result = await updateDigitalCard(cardId, cardData);
      
      alert('Card updated successfully! Redirecting...');
      navigate('/digital-cards', { replace: true });
      setTimeout(() => {
        window.location.href = '/digital-cards';
      }, 500);
      
    } catch (error) {
      console.error('❌ Error updating card:', error);
      console.error('❌ Error stack:', error.stack);
      
      // Show detailed error
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      alert(`Failed to update card:\n${errorMsg}\n\nCheck console for details.`);
    }
  };

  const handleCancel = () => {
    navigate('/digital-cards');
  };

  if (loading) {
    return <div className="loading">Loading card data...</div>;
  }

  if (!card) {
    return (
      <div className="not-found">
        <h2>Card not found</h2>
        <Link to="/digital-cards" className="btn btn-primary">
          Back to Cards
        </Link>
      </div>
    );
  }

  return (
    <DigitalIDForm
      editingCard={card}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );
};

const DigitalCardView = ({ cardId }) => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCard = async () => {
      try {
        const cards = await getDigitalCards();
        const cardData = (cards || []).find(c => c.id == cardId);
        setCard(cardData);
      } catch (error) {
        console.error('Error loading card:', error);
        setCard(null);
      }
      setLoading(false);
    };
    
    loadCard();
  }, [cardId]);

  if (loading) {
    return <div className="loading">Loading card...</div>;
  }

  if (!card) {
    return (
      <div className="not-found">
        <h2>Card not found</h2>
        <Link to="/digital-cards" className="btn btn-primary">
          Back to Cards
        </Link>
      </div>
    );
  }

  return (
    <div className="card-view">
      <div className="view-header">
        <h2>Digital ID Card - {card.name}</h2>
        <div className="view-actions">
          <Link to={`/digital-cards/edit/${card.id}`} className="btn btn-primary">
            Edit Card
          </Link>
          <Link to="/digital-cards" className="btn btn-secondary">
            Back to List
          </Link>
        </div>
      </div>
      
      <div className="card-display">
        <DigitalIDCard cardData={card} />
      </div>
    </div>
  );
};

const DigitalCardManagement = () => {
  return (
    <Routes>
      <Route path="/" element={<DigitalCardList />} />
      <Route path="/create" element={<DigitalCardCreate />} />
      <Route path="/edit/:cardId" element={<DigitalCardEditWrapper />} />
      <Route path="/view/:cardId" element={<DigitalCardViewWrapper />} />
      <Route path="*" element={<Navigate to="/digital-cards" />} />
    </Routes>
  );
};

// Wrapper components to extract route params
const DigitalCardEditWrapper = () => {
  const { cardId } = useParams();
  return <DigitalCardEdit cardId={parseInt(cardId)} />;
};

const DigitalCardViewWrapper = () => {
  const { cardId } = useParams();
  return <DigitalCardView cardId={parseInt(cardId)} />;
};

export default DigitalCardManagement;
